import { useEffect, useState } from "react";
import { UserPlus, Copy, Check, Pencil } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { CONTRACTOR_ROLES, roleLabel } from "@/constants/roles";
import SearchableSelect from "@/components/SearchableSelect";
import RampUserImport from "@/components/RampUserImport";
import { useAuth } from "@/contexts/AuthContext";

const STATUS_LABELS = { active: "Active", inactive: "Inactive", pending_invite: "Invite Sent" };

const RESTRICTION_LABELS = {
  none: "Never requires approval", all_purchases: "Always requires approval", threshold: "Requires approval over a threshold",
};
const policySummary = (p) => {
  if (!p) return "Company rules (no override)";
  if (p.restriction === "threshold") return `Threshold: over $${Number(p.threshold_amount || 0).toLocaleString("en-US")}`;
  return RESTRICTION_LABELS[p.restriction] || p.restriction;
};

export default function Team() {
  const [team, setTeam] = useState([]);
  // Offered once Ramp is connected with the users permission (admins only).
  const [rampUsers, setRampUsers] = useState(false);
  const [show, setShow] = useState(false);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [role, setRole] = useState("project_manager");
  const [inviteLink, setInviteLink] = useState(null);
  const [copied, setCopied] = useState(false);
  const [editing, setEditing] = useState(null);
  const [editRole, setEditRole] = useState("");
  const [editStatus, setEditStatus] = useState("");
  const [editOverride, setEditOverride] = useState(false); // false = "use company rules" (clears the policy)
  const [editRestriction, setEditRestriction] = useState("none");
  const [editThreshold, setEditThreshold] = useState("");
  const [editChainRoles, setEditChainRoles] = useState([]);

  const { user } = useAuth();
  const load = () => api.get("/contractor/team").then((r) => setTeam(r.data));
  useEffect(() => { load(); }, []);
  useEffect(() => {
    if (user?.role !== "company_admin") return;
    api.get("/contractor/integrations").then((r) => {
      const row = r.data.find((i) => i.type === "ramp" && i.status === "connected");
      setRampUsers(!!row && !!(row.capabilities || {}).users);
    }).catch(() => {});
  }, [user?.role]);

  const openEdit = (m) => {
    setEditing(m);
    setEditRole(m.role);
    setEditStatus(m.status === "pending_invite" ? "active" : m.status);
    setEditOverride(!!m.purchase_policy);
    setEditRestriction(m.purchase_policy?.restriction || "none");
    setEditThreshold(m.purchase_policy?.threshold_amount ?? "");
    setEditChainRoles(m.purchase_policy?.approver_role_chain || []);
  };

  const toggleChainRole = (r) => setEditChainRoles((rs) => (rs.includes(r) ? rs.filter((x) => x !== r) : [...rs, r]));

  const saveEdit = async () => {
    if (editOverride && editRestriction === "threshold" && editThreshold === "") {
      return toast.error("Enter a threshold amount");
    }
    const body = {
      role: editRole, status: editStatus,
      purchase_policy: editOverride
        ? { restriction: editRestriction, threshold_amount: editRestriction === "threshold" ? Number(editThreshold) : null, approver_role_chain: editChainRoles }
        : null,
    };
    try {
      await api.patch(`/contractor/team/${editing.id}`, body);
      toast.success("Team member updated");
      setEditing(null);
      load();
    } catch (e) {
      const detail = e.response?.data?.detail;
      toast.error(Array.isArray(detail) ? detail.map((d) => d.message).join(" ") : detail || "Failed to update");
    }
  };

  const invite = async () => {
    if (!name || !email) return toast.error("Name and email required");
    try {
      const { data } = await api.post("/contractor/team", { name, email, role, job_ids: [] });
      setInviteLink(`${window.location.origin}/accept-invite?token=${data.invite_token}`);
      setName(""); setEmail("");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to invite");
    }
  };

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(inviteLink);
      setCopied(true);
      toast.success("Invite link copied");
      setTimeout(() => setCopied(false), 2000);
    } catch (e) {
      toast.error("Couldn't access the clipboard — select and copy the link manually");
    }
  };

  const closeInviteModal = () => {
    setShow(false);
    setInviteLink(null);
    setCopied(false);
  };

  const toggleStatus = async (member) => {
    if (member.status === "pending_invite") return; // must accept the invite first
    const status = member.status === "active" ? "inactive" : "active";
    await api.patch(`/contractor/team/${member.id}`, { status });
    load();
  };

  return (
    <div className="p-6 md:p-8" data-testid="team-page">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h1 className="text-2xl font-extrabold tracking-tight">Team & Permissions</h1>
          <p className="text-slate-500 text-sm mt-1">Manage roles and access for your company.</p>
        </div>
        <div className="flex items-center gap-2">
          {rampUsers && <RampUserImport onImported={load} />}
          <button onClick={() => setShow(true)} data-testid="invite-member-button" className="flex items-center gap-2 px-4 py-2.5 rounded-lg bg-sky-500 hover:bg-sky-600 text-white text-[13px] font-semibold">
            <UserPlus size={14} /> Invite Member
          </button>
        </div>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full mt-7 text-[13px]" data-testid="team-table">
          <thead>
            <tr className="text-[11px] uppercase tracking-wide text-slate-400 font-semibold border-b border-slate-100">
              <th className="text-left py-2.5">Name</th><th className="text-left py-2.5">Email</th><th className="text-left py-2.5">Role</th><th className="text-left py-2.5">Purchase Policy</th><th className="text-left py-2.5">Status</th><th></th>
            </tr>
          </thead>
          <tbody>
            {team.map((m) => (
              <tr key={m.id} className="border-b border-slate-50" data-testid={`team-row-${m.id}`}>
                <td className="py-3 font-medium">{m.name}</td>
                <td className="py-3 text-slate-500">{m.email}</td>
                <td className="py-3">{roleLabel(m.role)}</td>
                <td className="py-3 text-slate-500 text-[12.5px]">{policySummary(m.purchase_policy)}</td>
                <td className="py-3">{STATUS_LABELS[m.status] || m.status}</td>
                <td className="py-3 text-right whitespace-nowrap">
                  {m.status !== "pending_invite" && (
                    <>
                      <button onClick={() => openEdit(m)} data-testid={`edit-member-${m.id}`} className="text-slate-400 hover:text-sky-600 mr-3"><Pencil size={14} /></button>
                      <button onClick={() => toggleStatus(m)} data-testid={`toggle-status-${m.id}`} className="text-[12px] font-semibold text-sky-600">
                        {m.status === "active" ? "Deactivate" : "Activate"}
                      </button>
                    </>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Dialog open={show} onOpenChange={(open) => (open ? setShow(true) : closeInviteModal())}>
        <DialogContent data-testid="invite-member-modal">
          {!inviteLink ? (
            <>
              <DialogHeader><DialogTitle>Invite Team Member</DialogTitle></DialogHeader>
              <div className="space-y-3 mt-2">
                <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Full name" data-testid="invite-name-input" className="w-full h-10 px-3 rounded-lg border border-slate-200 text-sm" />
                <input value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Work email" data-testid="invite-email-input" className="w-full h-10 px-3 rounded-lg border border-slate-200 text-sm" />
                <SearchableSelect value={role} onChange={(e) => setRole(e.target.value)} data-testid="invite-role-select" className="w-full h-10 px-3 rounded-lg border border-slate-200 text-sm">
                  {CONTRACTOR_ROLES.map((r) => <option key={r.value} value={r.value}>{r.label}</option>)}
                </SearchableSelect>
                <button onClick={invite} data-testid="confirm-invite-button" className="w-full h-10 rounded-lg bg-sky-500 text-white text-[13px] font-semibold">Send Invite</button>
              </div>
            </>
          ) : (
            <>
              <DialogHeader><DialogTitle>Invite Created</DialogTitle></DialogHeader>
              <div className="space-y-3 mt-2">
                <p className="text-[13px] text-slate-500">Share this link with the new team member — it expires in 7 days and can only be used once.</p>
                <div className="flex items-center gap-2">
                  <input readOnly value={inviteLink} data-testid="invite-link-input" className="flex-1 h-10 px-3 rounded-lg border border-slate-200 text-[12.5px] font-mono text-slate-600" />
                  <button onClick={copyLink} data-testid="copy-invite-link-button" className="h-10 px-3 rounded-lg border border-slate-200 hover:bg-slate-50 flex items-center gap-1.5 text-[12.5px] font-semibold shrink-0">
                    {copied ? <Check size={14} /> : <Copy size={14} />} {copied ? "Copied" : "Copy Link"}
                  </button>
                </div>
                <button onClick={closeInviteModal} data-testid="close-invite-modal-button" className="w-full h-10 rounded-lg bg-slate-900 text-white text-[13px] font-semibold">Done</button>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={!!editing} onOpenChange={(open) => !open && setEditing(null)}>
        <DialogContent data-testid="edit-member-modal">
          <DialogHeader><DialogTitle>Edit {editing?.name}</DialogTitle></DialogHeader>
          <div className="space-y-3 mt-2 max-h-[70vh] overflow-y-auto ss-scrollbar pr-1">
            <div>
              <label className="text-[13px] font-medium">Role</label>
              <SearchableSelect value={editRole} onChange={(e) => setEditRole(e.target.value)} data-testid="edit-role-select" className="mt-1.5 w-full h-10 px-3 rounded-lg border border-slate-200 text-sm">
                {CONTRACTOR_ROLES.map((r) => <option key={r.value} value={r.value}>{r.label}</option>)}
              </SearchableSelect>
            </div>
            <div>
              <label className="text-[13px] font-medium">Status</label>
              <SearchableSelect value={editStatus} onChange={(e) => setEditStatus(e.target.value)} data-testid="edit-status-select" className="mt-1.5 w-full h-10 px-3 rounded-lg border border-slate-200 text-sm">
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </SearchableSelect>
            </div>

            <div className="border border-slate-200 rounded-lg p-3 space-y-2.5">
              <label className="flex items-center gap-2 text-[13px] font-medium">
                <input type="checkbox" checked={editOverride} onChange={(e) => setEditOverride(e.target.checked)} data-testid="edit-policy-override-checkbox" />
                Override this person's purchase approval policy
              </label>
              <p className="text-[11.5px] text-slate-400">Off = follow company approval rules only, same as everyone else.</p>
              {editOverride && (
                <>
                  <SearchableSelect value={editRestriction} onChange={(e) => setEditRestriction(e.target.value)} data-testid="edit-restriction-select" className="w-full h-9 px-2.5 rounded-lg border border-slate-200 text-[12.5px]">
                    <option value="none">Never requires approval</option>
                    <option value="all_purchases">Always requires approval</option>
                    <option value="threshold">Requires approval over a threshold</option>
                  </SearchableSelect>
                  {editRestriction === "threshold" && (
                    <input type="number" min="0" value={editThreshold} onChange={(e) => setEditThreshold(e.target.value)} placeholder="Threshold amount ($)" data-testid="edit-threshold-input" className="w-full h-9 px-2.5 rounded-lg border border-slate-200 text-[12.5px]" />
                  )}
                  {editRestriction !== "none" && (
                    <div>
                      <label className="text-[12px] text-slate-500">Approver chain (blank = company admin)</label>
                      <div className="mt-1.5 flex flex-wrap gap-2">
                        {CONTRACTOR_ROLES.map((r) => (
                          <label key={r.value} className="flex items-center gap-1.5 text-[12px] border border-slate-200 rounded-full px-2.5 py-1">
                            <input type="checkbox" checked={editChainRoles.includes(r.value)} onChange={() => toggleChainRole(r.value)} data-testid={`edit-chain-role-${r.value}`} />
                            {r.label}
                          </label>
                        ))}
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>

            <button onClick={saveEdit} data-testid="save-member-edit-button" className="w-full h-10 rounded-lg bg-sky-500 text-white text-[13px] font-semibold">Save Changes</button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
